import { useContext, useEffect, useRef, useState } from 'react';
import { getTours } from 'services/package';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { PackageSearch, PackagesResponse } from 'ts/package/interfaces/Package';
import PackagesList from './PackagesList';
import ResultTopBar from './ResultTopBar';
import { getFilteredPackages, getSortedPackages } from 'utils/package';
import PackageFilter from './PackageFilter';
import { PackageFilterModel } from 'ts/package/classes/PackageFilterModel';
import PackagesResultSkeleton from './PackagesResultSkeleton';
import ExpiredTimeout from 'components/shared/ExpiredTimeout';
import * as idb from 'utils/shared/idbUrl';
import usePageInMemory from 'hooks/usePageInMemory';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useGetPage } from 'hooks/useGetPage';
import { SearchTourModel } from 'ts/package/classes/SearchTourModel';
import SearchTours from '../Search/SearchTours';
import useRecaptcha from 'hooks/useRecaptcha';
import { useTranslation } from 'react-i18next';
import { openInNewWindow } from 'utils/utils';
import usePackageDataLayer from 'hooks/usePackageDataLayer';
import { MainContext } from 'contexts/MainContext';

const ResultTourPage = () => {
  const [urlParams] = useSearchParams();
  const [response, setResponse] = useState<PackagesResponse>();
  const [packages, setPackages] = useState<PackageSearch[]>([]);
  const [filter, setFilter] = useState<PackageFilterModel>();
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const showModalTimeoutRef = useRef<any>();
  const daysDiff = useRef(0);
  const { handleRecapcha, isRecaptchaReady } = useRecaptcha();
  const { t } = useTranslation();
  const [sort, setSort] = useState('');
  const { userSiteConfig } = useContext(MainContext);
  const navigate = useNavigate();
  const location = useLocation();
  const idbKey = location.pathname + location.search;
  const stateRef = useRef<any>();
  const { pushImpression } = usePackageDataLayer(true);
  stateRef.current = {
    packages,
    filter,
    response,
    sort,
  };
  useGetPage('ILPackages/Search');
  usePageInMemory(idbKey, () => saveState());

  useEffect(() => {
    (async () => {
      const savedState = await idb.getPageState(idbKey);
      if (savedState && savedState.response) {
        setPackages(savedState.packages);
        setFilter(savedState.filter);
        setResponse(savedState.response);
        setSort(savedState.sort);

        idb.deletePageState(idbKey);
      } else if (isRecaptchaReady) {
        setResponse(undefined);
        setPackages([]);
        setFilter(undefined);
        const recapchaToken = await handleRecapcha();
        if (!recapchaToken) return;
        const params = new SearchTourModel(urlParams);
        const newResponse = await getTours(params, recapchaToken);
        const newPackages = getSortedPackages(
          newResponse?.deals || [],
          sessionStorage.getItem('tourSort') || 'priceIncrease'
        );
        daysDiff.current = +(newResponse?.deals[0]?.dealLength || '0');
        const newFilter = new PackageFilterModel(newPackages, daysDiff.current);
        const filteredPackages = getFilteredPackages(newFilter, newPackages);

        setPackages(filteredPackages);
        setResponse(newResponse);
        setFilter(newFilter);
        pushImpression(newResponse?.deals || []);
      }
    })();
    clearTimeout(showModalTimeoutRef.current);
    showModalTimeoutRef.current = setTimeout(() => {
      setShowExpiredModal(true);
    }, 20 * 60 * 1000);
  }, [urlParams, isRecaptchaReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSortChange = (value: string) => {
    const sortedPackages = [...getSortedPackages(response?.deals || [], value)];
    setPackages(sortedPackages);
    setSort(value);
  };

  const onClickResetFilter = () => {
    const newFilter = new PackageFilterModel(packages, daysDiff.current);

    setFilter(newFilter);
    setPackages(getFilteredPackages(newFilter, packages));
  };

  const onChangeFilter = (newFilter: PackageFilterModel) => {
    const newPackages = getFilteredPackages(newFilter, packages);

    setFilter(newFilter);
    setPackages(newPackages);
  };
  const onClickChoose = (pac: PackageSearch) => {
    const params = Object.fromEntries(urlParams);
    const data = {
      checkIn: params.checkIn,
      cityCode: params.cityCode.split('-')[0],
      dealId: pac.dealId,
      hotelId: pac.hotelId,
      type: params.type || '1',
      provider: pac.provider,
      rowId: pac.rowId,
      isSale: 'false',
      paxComplect: pac.paxComplect,
      name: pac.rowName,
      searchToken: sessionStorage.getItem('searchTourToken') || '',
      discountId: pac.discountId + '',
    };

    if (userSiteConfig.base.openDetailsInNewTab) {
      openInNewWindow('/tour/details?' + new URLSearchParams(data).toString());
    } else {
      saveState();
      navigate({ pathname: '/tour/details', search: new URLSearchParams(data).toString() });
    }
  };
  const saveState = () => {
    idb.savePageState(idbKey, stateRef.current);
  };

  return (
    <div className="results-main">
      <SearchTours />
      <ResultTopBar
        onSortChange={onSortChange}
        filter={filter}
        onChangeFilter={onChangeFilter}
        sessionKey={'tourSort'}
        newSort={sort}
      />
      {!!packages.length && (
        <div className="packages-result-wr results-wr">
          {!!filter && (
            <PackageFilter
              filter={filter}
              onChangeFilter={onChangeFilter}
              onClickReset={onClickResetFilter}
              isTour={true}
            />
          )}
          <PackagesList
            packages={packages.filter((pac) => pac.visible)}
            onClickChoose={onClickChoose}
          />
        </div>
      )}
      {!response && <PackagesResultSkeleton />}
      {showExpiredModal && (
        <ExpiredTimeout navigatePath="/tours" onClose={() => setShowExpiredModal(false)} />
      )}
      {!!response && !response.deals.length && <div className="no-entries">{t('no_entries')}</div>}
    </div>
  );
};

export default withErrorBoundary(ResultTourPage, 'ResultTourPage');
